import { initializeApp } from "firebase/app";
import { getAuth, signInWithEmailAndPassword, onAuthStateChanged, signOut, type User } from "firebase/auth";
import {
  getFirestore,
  collection,
  doc,
  getDoc,
  query,
  orderBy,
  limit,
  onSnapshot,
  type DocumentData,
  type DocumentReference,
  type Timestamp,
  type Unsubscribe,
} from "firebase/firestore";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface RoomState {
  identifier: string;
  location: string;
  motion: number | null;
  illuminance: number | null;
  lastSeen: Date | null;
  status: "occupied" | "vacant" | "unknown";
  // Per-sensor motion tracking for rooms with multiple sensors.
  // Key = values doc ID, value = most recent motion reading.
  sensorMotion: Map<string, number | null>;
}

// ---------------------------------------------------------------------------
// Firebase config
// ---------------------------------------------------------------------------

const firebaseConfig = {
  apiKey: "AIzaSyC6rWNeLXtlHO8rnZ2rsI8Bpmgnw8ui1us",
  authDomain: "stc-production-150269.firebaseapp.com",
  projectId: "stc-production-150269",
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);

// ---------------------------------------------------------------------------
// Room configuration
// ---------------------------------------------------------------------------

// The observation that groups the motion sensor devices
const OBSERVATION_ID = "Z0nqdUCk7jF9jjGGhjQ2";

// Stable mapping: device identifier → _values doc ID for motion sensor data.
// These are infrastructure IDs that don't change. Room names (locations) are
// read dynamically from the _device documents in Firestore.
const IDENTIFIER_TO_VALUES: ReadonlyMap<string, string> = new Map([
  ["shl_040d84e84997", "Ev3bQqBxB1gy1SJIh4rT"],
  ["shl_b0c7de792a0a", "xYIrX4pBwTt1kAYXWGGk"],
  ["shl_0cae5f62b860", "dIhfznh3Fe9MJAdwk1hL"],
  ["shl_040d84e84b02", "mbmJOSomWHMD8oQ53A7U"],
]);

// Secondary sensors that share a room with a primary sensor.
// The room tile uses the primary sensor's identity/location, but shows
// "occupied" if ANY sensor in the group detects motion.
const SECONDARY_SENSORS: ReadonlyMap<string, string> = new Map([
  // shl_b0c7de6cb925 shares the room with primary shl_040d84e84997
  ["shl_b0c7de6cb925", "TQEymRvT45xq2OzKHrTk"],
]);

// Primary identifier → set of additional values doc IDs to monitor
const PAIRED_SENSORS: ReadonlyMap<string, string[]> = new Map([
  ["shl_040d84e84997", ["TQEymRvT45xq2OzKHrTk"]],
]);

const STATUS_CONFIG: Record<RoomState["status"], { label: string; color: string; bg: string }> = {
  occupied: { label: "Occupied", color: "#d97706", bg: "#fffbeb" },
  vacant:   { label: "Vacant",   color: "#059669", bg: "#ecfdf5" },
  unknown:  { label: "Unknown",  color: "#6b7280", bg: "#f3f4f6" },
};

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

const roomStates: Map<string, RoomState> = new Map();
const listeners: Unsubscribe[] = [];

// ---------------------------------------------------------------------------
// DOM elements
// ---------------------------------------------------------------------------

const loginScreen = document.getElementById("loginScreen")!;
const dashboardScreen = document.getElementById("dashboardScreen")!;
const loginForm = document.getElementById("loginForm") as HTMLFormElement;
const emailInput = document.getElementById("emailInput") as HTMLInputElement;
const passwordInput = document.getElementById("passwordInput") as HTMLInputElement;
const loginError = document.getElementById("loginError")!;
const loginBtn = document.getElementById("loginBtn") as HTMLButtonElement;
const logoutBtn = document.getElementById("logoutBtn") as HTMLButtonElement;
const grid = document.getElementById("roomGrid")!;
const statusEl = document.getElementById("status")!;

// ---------------------------------------------------------------------------
// Login
// ---------------------------------------------------------------------------

loginForm.addEventListener("submit", async (e: Event) => {
  e.preventDefault();
  loginBtn.disabled = true;
  loginError.textContent = "";

  try {
    await signInWithEmailAndPassword(auth, emailInput.value, passwordInput.value);
  } catch (err) {
    loginError.textContent = err instanceof Error ? err.message : "Login failed";
    loginBtn.disabled = false;
  }
});

logoutBtn.addEventListener("click", () => {
  signOut(auth);
});

// ---------------------------------------------------------------------------
// Rendering
// ---------------------------------------------------------------------------

/** Build a display string of all sensor identifiers for a room. */
function getAllIdentifiers(primaryIdentifier: string): string {
  const ids = [primaryIdentifier];
  for (const [secId, secValuesDoc] of SECONDARY_SENSORS) {
    const pairedExtras = PAIRED_SENSORS.get(primaryIdentifier);
    if (pairedExtras?.includes(secValuesDoc)) {
      ids.push(secId);
    }
  }
  return ids.join(", ");
}

function render(): void {
  grid.innerHTML = "";

  for (const room of roomStates.values()) {
    const config = STATUS_CONFIG[room.status];
    const card = document.createElement("div");
    card.className = "room-card";
    card.style.borderColor = config.color;
    card.style.backgroundColor = config.bg;

    const lastSeen = room.lastSeen
      ? room.lastSeen.toLocaleString("en-GB", {
          day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
        })
      : "Never";

    const lightOn = room.illuminance !== null && room.illuminance >= 100;
    const bulbColor = lightOn ? "#fbbf24" : "#d1d5db";
    const bulbStroke = lightOn ? "#d97706" : "#9ca3af";

    card.innerHTML = `
      <div class="room-card-inner">
        <div class="room-info">
          <div class="room-name">${room.location}</div>
          <div class="room-status" style="color: ${config.color}">${config.label}</div>
          <div class="room-detail">Last activity: ${lastSeen}</div>
          <div class="room-detail">${getAllIdentifiers(room.identifier)}</div>
        </div>
        <div class="room-bulb">
          <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="${bulbStroke}" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M9 18h6" />
            <path d="M10 22h4" />
            <path fill="${bulbColor}" d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 0 1 8.91 14" />
          </svg>
        </div>
      </div>
    `;

    grid.appendChild(card);
  }

  statusEl.textContent = `Live \u2014 ${new Date().toLocaleTimeString("en-GB", {
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  })}`;
}

// ---------------------------------------------------------------------------
// Room discovery — reads observation + device docs from Firestore
// ---------------------------------------------------------------------------

async function discoverRooms(): Promise<void> {
  roomStates.clear();

  const obsSnap = await getDoc(doc(db, "_observation", OBSERVATION_ID));
  if (!obsSnap.exists()) {
    console.error("Observation not found:", OBSERVATION_ID);
    return;
  }

  const obsData = obsSnap.data();
  const deviceEntries: { reference: DocumentReference }[] = obsData.device || [];

  for (const entry of deviceEntries) {
    const ref = entry.reference;
    if (!ref) continue;

    const deviceSnap = await getDoc(ref);
    if (!deviceSnap.exists()) continue;

    const deviceData = deviceSnap.data();
    const identifier: string = deviceData.identifier || "";
    const location: string = deviceData.location || identifier;
    const valuesDocId = IDENTIFIER_TO_VALUES.get(identifier);

    // Skip secondary sensors — they feed into a primary sensor's tile
    if (SECONDARY_SENSORS.has(identifier)) continue;

    if (!valuesDocId) {
      console.warn("No values mapping for device:", identifier);
      continue;
    }

    roomStates.set(valuesDocId, {
      identifier,
      location,
      motion: null,
      illuminance: null,
      lastSeen: null,
      status: "unknown",
      sensorMotion: new Map(),
    });

    // Listen for device doc changes (e.g. room name edits) in real-time
    listeners.push(
      onSnapshot(ref, (snap) => {
        if (!snap.exists()) return;
        const data = snap.data();
        const state = roomStates.get(valuesDocId);
        if (state) {
          state.location = data.location || state.identifier;
          render();
        }
      })
    );
  }
}

// ---------------------------------------------------------------------------
// Real-time value listeners
// ---------------------------------------------------------------------------

/** Recompute a room's aggregate status from all its sensor readings. */
function updateRoomStatus(state: RoomState): void {
  if (state.sensorMotion.size === 0) {
    state.status = "unknown";
    return;
  }

  const values = [...state.sensorMotion.values()];
  if (values.every((v) => v === null)) {
    state.status = "unknown";
  } else if (values.some((v) => v === 1)) {
    state.status = "occupied";
  } else {
    state.status = "vacant";
  }
}

/** Subscribe to a single values subcollection and feed results into a room. */
function subscribeToSensor(valuesDocId: string, state: RoomState, isPrimary: boolean): void {
  const valuesRef = collection(db, "_values", valuesDocId, "values");
  const q = query(valuesRef, orderBy("deviceTimestamp", "desc"), limit(1));

  state.sensorMotion.set(valuesDocId, null);

  listeners.push(
    onSnapshot(q, (snapshot) => {
      if (snapshot.empty) {
        state.sensorMotion.set(valuesDocId, null);
      } else {
        const data: DocumentData = snapshot.docs[0].data();
        const motion = data.motion as number;
        const ts = data.deviceTimestamp as Timestamp;

        state.sensorMotion.set(valuesDocId, motion);

        // Primary sensor owns the illuminance and lastSeen fields
        if (isPrimary) {
          state.illuminance = (data.illuminance as number) ?? null;
          state.lastSeen = ts.toDate();
        } else {
          // Secondary sensor can push lastSeen forward if it's more recent
          const sensorTime = ts.toDate();
          if (!state.lastSeen || sensorTime > state.lastSeen) {
            state.lastSeen = sensorTime;
          }
        }
      }

      updateRoomStatus(state);
      render();
    }, (err) => {
      console.error(`Listener error for ${state.identifier} (${valuesDocId}):`, err);
      state.sensorMotion.set(valuesDocId, null);
      updateRoomStatus(state);
      render();
    })
  );
}

function subscribeToValues(): void {
  for (const [valuesDocId, state] of roomStates) {
    // Subscribe to the primary sensor
    subscribeToSensor(valuesDocId, state, true);

    // Subscribe to any paired secondary sensors
    const extras = PAIRED_SENSORS.get(state.identifier);
    if (extras) {
      for (const extraDocId of extras) {
        subscribeToSensor(extraDocId, state, false);
      }
    }
  }
}

async function startListeners(): Promise<void> {
  statusEl.textContent = "Loading rooms\u2026";
  await discoverRooms();
  subscribeToValues();
  render();
}

function stopListeners(): void {
  for (const unsub of listeners) {
    unsub();
  }
  listeners.length = 0;
}

// ---------------------------------------------------------------------------
// Auth state
// ---------------------------------------------------------------------------

function showLogin(): void {
  loginScreen.style.display = "flex";
  dashboardScreen.style.display = "none";
  loginBtn.disabled = false;
  passwordInput.value = "";
  loginError.textContent = "";
}

function showDashboard(): void {
  loginScreen.style.display = "none";
  dashboardScreen.style.display = "block";
}

onAuthStateChanged(auth, (user: User | null) => {
  if (user) {
    showDashboard();
    startListeners().catch((err) => {
      console.error("Failed to start listeners:", err);
      statusEl.textContent = "Error loading rooms \u2014 check console";
    });
  } else {
    stopListeners();
    roomStates.clear();
    showLogin();
  }
});
